// Result of POST /audience/preview: total, by-department table, an expandable
// people list with each person's matched conditions as short labels, and the
// "N more outside your scope" note. Pure presentation — the numbers are the
// backend's; nothing is re-matched here.
import { useT } from '../lib/i18n';
import React from 'react';
import type { AudiencePreview } from '@/services/learningService';

export const AudiencePreviewPanel: React.FC<{ preview: AudiencePreview; className?: string }> = ({ preview, className }) => {
  const t = useT();
  return (
    <div className={`space-y-1.5 ${className || ''}`} data-testid="audience-preview">
      <div className="text-sm text-foreground">
        {t('training:wiz_preview_total', '{{n}} people match', { n: preview.total })}
        {preview.outside_scope > 0 ? <span className="text-muted-foreground"> · {t('training:wiz_outside_scope_more', '{{n}} more outside your scope', { n: preview.outside_scope })}</span> : null}
      </div>
      {preview.by_department.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-muted-foreground"><th className="text-left font-medium py-1">{t('training:wiz_department', 'Department')}</th><th className="text-right font-medium py-1">{t('training:wiz_count', 'People')}</th></tr></thead>
            <tbody>{preview.by_department.map((row) => <tr key={row.department || '—'} className="border-t border-border"><td className="py-1">{row.department || '—'}</td><td className="py-1 text-right tabular-nums">{row.count}</td></tr>)}</tbody>
          </table>
        </div>
      )}
      {preview.people.length > 0 && (
        <details>
          <summary className="cursor-pointer text-xs text-orange-600">{t('training:wiz_view_people', 'View people')}</summary>
          <ul className="mt-1 max-h-48 overflow-y-auto text-xs space-y-1">
            {preview.people.map((p) => (
              <li key={p.user_id} className="flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5">
                <span className="text-foreground">{p.name || p.user_id.slice(0, 8)}{p.manual ? <span className="text-muted-foreground"> ({t('training:wiz_manual', 'added')})</span> : null}</span>
                <span className="text-muted-foreground">{p.department}</span>
                {(p.matched_conditions || []).length > 0 && (
                  <span className="basis-full flex flex-wrap gap-1">
                    {p.matched_conditions.map((c, i) => <span key={`${c.field}-${i}`} className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">{c.label || c.value}</span>)}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};

export default AudiencePreviewPanel;
